/** The metric the Underwriting Quotes table is currently sorted by. */
export type QuoteSortBy = "APR" | "Monthly Payment";

/**
 * Translated from Flex's `compareMonthlyPriceByQuoteSort`: sort by the chosen metric
 * ascending, then fall through commission, affiliate commission, the other metric,
 * and finally lender name — so ties resolve identically to Flex.
 *
 * Fields are nullable here (unlike `@cf247/pricing`'s `MonthlyPrice`, which declares them
 * non-nullable) since real-world quote data doesn't always populate every figure; a
 * `MonthlyPrice` is still structurally assignable to this type.
 */
export type QuoteForSort = {
  effectiveRate?: number | null;
  monthlyPayment?: number | null;
  commissionAmount?: number | null;
  affiliateCommissionAmount?: number | null;
  lenderName?: string | null;
};

const byPaymentAsc = (a: QuoteForSort, b: QuoteForSort) => {
  if (a.monthlyPayment == null && b.monthlyPayment == null) return 0;
  return (a.monthlyPayment ?? Infinity) - (b.monthlyPayment ?? Infinity);
};

const byAprAsc = (a: QuoteForSort, b: QuoteForSort) => {
  if (a.effectiveRate == null && b.effectiveRate == null) return 0;
  return (a.effectiveRate ?? Infinity) - (b.effectiveRate ?? Infinity);
};

const byCommissionDesc = (a: QuoteForSort, b: QuoteForSort) =>
  (b.commissionAmount ?? 0) - (a.commissionAmount ?? 0);

const byAffiliateCommissionDesc = (a: QuoteForSort, b: QuoteForSort) =>
  (b.affiliateCommissionAmount ?? 0) - (a.affiliateCommissionAmount ?? 0);

const byLenderNameAsc = (a: QuoteForSort, b: QuoteForSort) =>
  (a.lenderName ?? "").localeCompare(b.lenderName ?? "");

const chainComparators =
  <T,>(...comparators: Array<(a: T, b: T) => number>) =>
  (a: T, b: T): number => {
    for (const compare of comparators) {
      const result = compare(a, b);
      if (result !== 0) return result;
    }
    return 0;
  };

const compareByMonthlyPayment = chainComparators<QuoteForSort>(
  byPaymentAsc,
  byCommissionDesc,
  byAffiliateCommissionDesc,
  byAprAsc,
  byLenderNameAsc
);

const compareByApr = chainComparators<QuoteForSort>(
  byAprAsc,
  byCommissionDesc,
  byAffiliateCommissionDesc,
  byPaymentAsc,
  byLenderNameAsc
);

/** Mirrors Flex's `compareMonthlyPriceByQuoteSort`, dispatching on the chosen sort metric. */
export function compareQuotes(a: QuoteForSort, b: QuoteForSort, sortBy: QuoteSortBy): number {
  return sortBy === "APR" ? compareByApr(a, b) : compareByMonthlyPayment(a, b);
}
